import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { fetchInChunks } from '@/lib/chunkIn';

// ============================================================================
// Oportunidade de convergência perdida — indicador de "quanto a pessoa está
// abrindo mão" por não ter convertido onde dava.
//
// Regra (Douglas, 01/06/2026): vendeu R$ 100 de fibra que podia ter levado
// móvel (ou vendeu só móvel onde tinha cobertura, sem fibra) => abre mão de
// 10% DESSE FATURAMENTO VENDIDO. Acumula por pessoa (consultor = ele; gestor =
// hierarquia). NÃO mexe na regra de comissão, no faturamento da comissão nem
// na meta — é só um indicador do que está sendo deixado na mesa.
//
// VISÃO 360 (unificada): as justificativas NÃO vivem numa tabela própria —
// elas já são capturadas pelo fluxo de Convergência do pedido e ficam no JSON
// `pedidos.convergencia` (+ timeline `movimento`). Este hook lê esse mesmo
// método e expõe os campos por papel (consultor/supervisor/gerência/BKO).
// ============================================================================

export type PapelJustificativa = 'consultor' | 'supervisor' | 'gerencia' | 'bko';
export const PAPEIS_JUSTIFICATIVA: PapelJustificativa[] = ['consultor', 'supervisor', 'gerencia', 'bko'];

// Um evento da timeline de convergência (tabela movimento) — FONTE ÚNICA do
// histórico: captura TUDO que já foi escrito (consultor/supervisor/gerência/bko),
// antigos e novos. Não há array paralelo em pedidos.convergencia.
export interface ConvEvento {
  created_at: string;
  papel: 'consultor' | 'supervisor' | 'gerencia' | 'bko' | 'sistema';
  texto: string;          // descrição completa do evento
  autor_nome: string | null;
}

// Visão 360 normalizada a partir do JSON pedidos.convergencia (estado atual)
// + a timeline `movimento` (histórico completo).
export interface Conv360 {
  // Timeline COMPLETA do banco (movimento) — FONTE ÚNICA do histórico.
  eventos: ConvEvento[];
  // Como a convergência foi decidida: 'gerente' | 'auto' | 'modal_cliente_tem_fibra' | 'modal_informou_movel'
  origem: string | null;
  // Consultor (texto livre OU declaração "já tem fibra/móvel")
  consultorMotivo: string | null;
  coberturaFibra: boolean | null;     // Claro/sistema diz que TEM cobertura
  cep: string | null;
  provaUrl: string | null;            // primeiro anexo (compat)
  provaUrls: string[];                // todos os anexos: prints (imagem) + gravação (áudio/mp3)
  fixaJaExistente: boolean | null;    // auto-declarou "já tem fibra"
  classificacao: string | null;       // SO_MOVEL | SO_FIBRA | CONVERGENTE
  // Supervisor
  justificativaSupervisor: string | null;
  // Gerência (decisão aprovar/reprovar)
  statusConv: string | null;          // 'aprovado sem fibra' | 'reprovado gerente' | ...
  motivoGerente: string | null;
  decididoEm: string | null;
  // BKO (validação)
  bkoValidacao: string | null;        // pendente | validado | reprovado
  bkoMotivo: string | null;
  divergenciaClaro: boolean | null;
}

export interface PedidoConvergenciaPerdida {
  pedido_id: string;
  cliente_id: string | null;
  cnpj: string;
  razao: string | null;
  equipe: string | null;
  consultor: string | null;
  tipo: 'SO_MOVEL' | 'SO_FIBRA';
  fat: number;
  // Contato do gestor do cliente (vw_cliente_gestor) p/ ação direta.
  gestor_nome: string | null;
  gestor_telefone: string | null;
  gestor_whatsapp: string | null;
  gestor_email: string | null;
  // Visão 360 vinda do método existente (pedidos.convergencia).
  conv: Conv360 | null;
  papeisRespondidos: PapelJustificativa[];
}

// Quais papéis já têm justificativa registrada (p/ o contador "X de 4").
function papeisComResposta(c: Conv360 | null): PapelJustificativa[] {
  if (!c) return [];
  const out: PapelJustificativa[] = [];
  if (c.consultorMotivo || c.provaUrl) out.push('consultor');
  if (c.justificativaSupervisor) out.push('supervisor');
  // Gerência só conta se REALMENTE decidiu (origem gerente) — não quando o sistema auto-resolveu.
  if (c.motivoGerente || c.origem === 'gerente') out.push('gerencia');
  if (c.bkoMotivo || (c.bkoValidacao && c.bkoValidacao !== 'pendente' && c.origem === 'gerente')) out.push('bko');
  return out;
}

// Vigência da regra de convergência: só vale a partir de junho/2026.
// Meses anteriores NÃO têm desconto retroativo (Douglas, 02/06/2026).
const CONVERGENCIA_VIGENCIA_INICIO = '2026-06';

// Cliente convergente (vendeu os dois lados) — lado positivo da balança.
// Um produto que compõe a convergência (lado móvel ou lado fibra).
export interface ItemConvergente {
  lado: 'movel' | 'fibra';
  produto: string;
  qtd: number;
  fat: number;
}

export interface ClienteConvergente {
  pedido_id?: string | null;
  cnpj: string;
  cliente_id: string | null;
  razao: string | null;
  equipe: string | null;
  consultor: string | null;
  fat: number;              // faturamento convergente do CNPJ no mês
  itens: ItemConvergente[]; // o que ela fez pra ser convergente (móvel + fibra)
  // Só nos "não pune": por que não foi penalizado.
  motivo?: 'sem_cobertura' | 'diretoria' | 'divergencia' | 'outro';
  tipo?: 'SO_MOVEL' | 'SO_FIBRA';
  /** Só no balde "declarado sem print": o contrato de fibra que o consultor alegou. */
  contrato?: string | null;
}

export interface ComissaoPerdidaConvergencia {
  penalidadePct: number;
  fatPenalizavel: number;   // faturamento vendido dos pedidos sem convergência
  perda: number;            // penalidadePct% desse faturamento (o que abre mão)
  pedidos: PedidoConvergenciaPerdida[];
  nPedidos: number;
  mesRef: string;           // 'YYYY-MM'
  // Lado POSITIVO (balança): acréscimo por convergência feita.
  bonusPct: number;
  fatBonificavel: number;   // faturamento dos clientes convergentes
  ganho: number;            // bonusPct% desse faturamento
  convergentes: ClienteConvergente[];
  nConvergentes: number;
  saldo: number;            // ganho − perda
  // NEUTROS: CNPJ que já era convergente de antes — novo pedido não ganha nem perde.
  neutros: ClienteConvergente[];
  nNeutros: number;
  // NÃO PUNE: SO_MOVEL/SO_FIBRA não penalizado (sem cobertura / Diretoria / divergência Claro).
  naoPune: ClienteConvergente[];
  /** Convergências que PAGAM bônus mas vêm de declaração validada SEM print. */
  declaradoSemPrint: ClienteConvergente[];
  nDeclaradoSemPrint: number;
  fatDeclaradoSemPrint: number;
  nNaoPune: number;
}

export function useComissaoPerdidaConvergencia(
  userId: string | undefined,
  competencia: Date,
  cargo: string = 'CONSULTOR',
) {
  const mes = format(competencia, 'yyyy-MM');
  const cargoCfg = (cargo || 'CONSULTOR').toUpperCase();
  return useQuery({
    queryKey: ['comissao-perdida-convergencia', userId, mes, cargoCfg],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev: any) => prev,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<ComissaoPerdidaConvergencia> => {
      // Sem desconto retroativo: meses antes da vigência (jun/2026) zeram.
      if (mes < CONVERGENCIA_VIGENCIA_INICIO) {
        return {
          penalidadePct: 0, fatPenalizavel: 0, perda: 0, pedidos: [], nPedidos: 0, mesRef: mes,
          bonusPct: 0, fatBonificavel: 0, ganho: 0, convergentes: [], nConvergentes: 0, saldo: 0,
          neutros: [], nNeutros: 0, naoPune: [], nNaoPune: 0,
          declaradoSemPrint: [], nDeclaradoSemPrint: 0, fatDeclaradoSemPrint: 0,
        };
      }

      // PERF (29/07): a hierarquia e a config NÃO dependem uma da outra — antes
      // eram duas idas em série. Vão juntas.
      //
      // 1) Consultores sob a pessoa (consultor = só ele; gestor = hierarquia).
      // 2) % do CARGO POR COMPETÊNCIA (carry-forward): pega a config mais recente
      //    com competencia <= mês visto. Assim cada mês tem seu %, meses futuros
      //    herdam o último até alguém mudar, e meses passados NÃO pegam regra nova.
      //    UM número controla os dois lados (desconta não-feita, acresce feita).
      const mesDate = `${mes}-01`;
      const [hierRes, cfgRes] = await Promise.all([
        supabase.rpc('get_consultores_por_hierarquia', {
          p_user_id: userId, p_competencia: mes,
        } as any).then(r => r, () => ({ data: null })),
        supabase
          .from('convergencia_comissao_config' as any)
          .select('penalidade_pct, ativo, competencia')
          .eq('cargo', cargoCfg)
          .lte('competencia', mesDate),
      ]);

      let consultorIds: string[] = userId ? [userId] : [];
      const idsHier = (hierRes.data as any[] | null)?.filter((c) => !c.fora_da_meta || c.consultor_id === userId).map((c) => c.consultor_id).filter(Boolean) ?? [];
      if (idsHier.length > 0) consultorIds = idsHier;

      const cfgRows = cfgRes.data;
      // Pega a competência mais recente <= mês (carry-forward) ordenando no cliente.
      const cfg = ((cfgRows as any[]) ?? [])
        .slice()
        .sort((a, b) => (String(a.competencia) < String(b.competencia) ? 1 : -1))[0] ?? null;
      const ativoCfg = cfg?.ativo !== false;
      const penalidadePct = (cfg && ativoCfg) ? Number(cfg.penalidade_pct ?? 0) : 0;
      const bonusPct = penalidadePct;

      // 3) Itens penalizáveis VENDIDOS (todos os estágios não-cancelados).
      //
      // PERF (29/07): as 3 views do lado positivo (bônus / neutros / não-pune) só
      // dependem de `consultorIds` — não do resultado desta. Antes esperavam esta
      // query terminar, e ela é a mais pesada (~700ms). Agora as 4 saem juntas: o
      // tempo de parede passa a ser o da mais lenta, não a soma.
      const [oportRes, bonusRes, neutroRes, npRes, declRes] = await Promise.all([
        supabase.from('v_convergencia_oportunidade' as any)
          .select('pedido_id, cliente_id, cnpj14, razao_social, equipe_nome, consultor_id, tipo_oportunidade, subtotal')
          .eq('mes_trabalho', mes)
          .eq('penalizavel', true)
          .in('consultor_id', consultorIds),
        supabase.from('v_convergencia_bonus' as any)
          .select('cnpj14, cliente_id, razao_social, equipe_nome, consultor_id, subtotal, familia, produto')
          .eq('mes_trabalho', mes).eq('bonificavel', true).in('consultor_id', consultorIds),
        supabase.from('v_convergencia_bonus' as any)
          .select('cnpj14, cliente_id, razao_social, equipe_nome, consultor_id, subtotal')
          .eq('mes_trabalho', mes).eq('convergente', true).lt('mes_convergencia', mes).in('consultor_id', consultorIds),
        supabase.from('v_convergencia_nao_pune' as any)
          .select('cnpj14, cliente_id, pedido_id, razao_social, equipe_nome, consultor_id, subtotal, motivo_nao_pune, tipo')
          .eq('mes_trabalho', mes).neq('motivo_nao_pune', 'diretoria').in('consultor_id', consultorIds),
        // (01/09/2026) DECLARADO SEM PRINT: o bônus que vem de "o cliente já tem
        // fibra" validado pelo BKO sem prova anexada. 861 declarações desde
        // junho, zero com print. Vira o balde reanalisável do lado do GANHO.
        supabase.from('v_convergencia_declaracao_sem_prova' as any)
          .select('cnpj14, pedido_id, cliente_id, razao_social, consultor_id, contrato_declarado, validado_por')
          .in('consultor_id', consultorIds),
      ]);
      const { data: rows, error } = oportRes;
      if (error) throw error;

      let fatPenalizavel = 0;
      const porPedido = new Map<string, PedidoConvergenciaPerdida & { consultor_id: string | null }>();
      for (const r of (rows as any[]) || []) {
        const sub = Number(r.subtotal || 0);
        fatPenalizavel += sub;
        const ex = porPedido.get(r.pedido_id);
        if (ex) ex.fat += sub;
        else porPedido.set(r.pedido_id, {
          pedido_id: r.pedido_id,
          cliente_id: r.cliente_id ?? null,
          cnpj: r.cnpj14,
          razao: r.razao_social ?? null,
          equipe: r.equipe_nome ?? null,
          consultor: null,
          consultor_id: r.consultor_id ?? null,
          tipo: r.tipo_oportunidade,
          fat: sub,
          gestor_nome: null, gestor_telefone: null, gestor_whatsapp: null, gestor_email: null,
          conv: null, papeisRespondidos: [],
        });
      }

      const pedidos = Array.from(porPedido.values()).sort((a, b) => b.fat - a.fat);

      // PERF: nomes + contato do gestor + convergência (flat) em PARALELO (3 → 1 ida).
      const clienteIds = [...new Set(pedidos.map(p => p.cliente_id).filter(Boolean))] as string[];
      const pedidoIds = pedidos.map(p => p.pedido_id);
      const [usRes, gestoresRes, pConvRes] = await Promise.all([
        consultorIds.length ? supabase.from('usuarios').select('id, nome').in('id', consultorIds) : Promise.resolve({ data: [] as any[] }),
        fetchInChunks(clienteIds, (parte) => supabase.from('vw_cliente_gestor' as any).select('cliente_id, gestor_nome, gestor_telefone, gestor_whatsapp, gestor_email').in('cliente_id', parte)),
        fetchInChunks(pedidoIds, (parte) => supabase.from('pedidos').select('id, convergencia').in('id', parte)),
      ]);
      const nomeById = new Map(((usRes.data as any[]) ?? []).map((u: any) => [u.id, u.nome]));
      for (const p of pedidos) p.consultor = p.consultor_id ? (nomeById.get(p.consultor_id) ?? null) : null;
      const porClienteGestor = new Map<string, any>();
      for (const g of (gestoresRes.data as any[]) || []) porClienteGestor.set(g.cliente_id, g);
      for (const p of pedidos) {
        const g = p.cliente_id ? porClienteGestor.get(p.cliente_id) : null;
        if (g) { p.gestor_nome = g.gestor_nome ?? null; p.gestor_telefone = g.gestor_telefone ?? null; p.gestor_whatsapp = g.gestor_whatsapp ?? null; p.gestor_email = g.gestor_email ?? null; }
      }

      // 4) VISÃO 360 (flat — chip/tooltip). Eventos vão SOB DEMANDA no modal.
      {
        const convById = new Map<string, any>();
        for (const r of (pConvRes.data as any[]) || []) convById.set(r.id, r.convergencia || null);

        // PERF: a timeline completa (eventos) é carregada SOB DEMANDA no modal — não
        // aqui — pra não pesar a página (eram 2 queries em `movimento` por TODOS os
        // pedidos + resolução de autores). O card só precisa dos campos flat (chip/tooltip).
        for (const p of pedidos) {
          const c = convById.get(p.pedido_id);
          if (!c) { p.conv = null; p.papeisRespondidos = []; continue; }
          // Quando o consultor DECLAROU (não escreveu texto livre), monta a declaração.
          const declaracao = (() => {
            if (c?.origem_decisao === 'modal_cliente_tem_fibra') {
              const f = c?.fixa_ja_existente;
              const det = f ? ` (contrato ${f.numeroContrato || '-'}${f.endereco ? ` · ${f.endereco}` : ''})` : '';
              return `Declarou que o cliente JÁ TEM FIBRA${det}`;
            }
            if (c?.origem_decisao === 'modal_informou_movel') {
              return `Declarou que o cliente JÁ TEM MÓVEL (${c?.qtd_linhas_movel ?? '-'} linhas${c?.territorio_aace ? ` · ${c.territorio_aace}` : ''})`;
            }
            return null;
          })();
          const conv: Conv360 = {
            eventos: [],
            origem: c?.origem_decisao ?? null,
            consultorMotivo: c?.motivo ?? declaracao ?? null,
            coberturaFibra: typeof c?.cobertura_fibra === 'boolean' ? c.cobertura_fibra : null,
            cep: c?.cep ?? null,
            provaUrl: c?.prova_url ?? null,
            provaUrls: Array.isArray(c?.prova_urls) && c.prova_urls.length ? c.prova_urls : (c?.prova_url ? [c.prova_url] : []),
            fixaJaExistente: typeof c?.fixa_ja_existente === 'boolean' ? c.fixa_ja_existente : null,
            classificacao: c?.classificacao ?? null,
            justificativaSupervisor: c?.justificativa_supervisor ?? null,
            statusConv: c?.status ?? null,
            motivoGerente: c?.motivo_gerente ?? null,
            decididoEm: c?.decidido_em ?? null,
            bkoValidacao: c?.bko_validacao ?? null,
            bkoMotivo: c?.bko_motivo ?? null,
            divergenciaClaro: typeof c?.divergencia_claro === 'boolean' ? c.divergencia_claro : null,
          };
          p.conv = conv;
          p.papeisRespondidos = papeisComResposta(conv);
        }
      }

      // 5/6/7) Convergentes (ganho) + neutros (já convergente) + não-pune.
      // As consultas já saíram lá em cima, junto com a de oportunidade.
      const bonusRows = bonusRes.data;

      const FAM_MOVEL = new Set(['Móvel', 'Totalização']);
      let fatBonificavel = 0;
      const porCnpj = new Map<string, ClienteConvergente & { consultor_id: string | null; _itens: Map<string, ItemConvergente> }>();
      for (const r of (bonusRows as any[]) || []) {
        const sub = Number(r.subtotal || 0);
        fatBonificavel += sub;
        const lado: 'movel' | 'fibra' = FAM_MOVEL.has(String(r.familia)) ? 'movel' : 'fibra';
        const produto = String(r.produto || '—');
        let ex = porCnpj.get(r.cnpj14);
        if (!ex) {
          ex = {
            cnpj: r.cnpj14, cliente_id: r.cliente_id ?? null, razao: r.razao_social ?? null, equipe: r.equipe_nome ?? null,
            consultor: null, consultor_id: r.consultor_id ?? null, fat: 0, itens: [],
            _itens: new Map<string, ItemConvergente>(),
          };
          porCnpj.set(r.cnpj14, ex);
        }
        ex.fat += sub;
        const key = `${lado}|${produto}`;
        const it = ex._itens.get(key);
        if (it) { it.qtd += 1; it.fat += sub; }
        else ex._itens.set(key, { lado, produto, qtd: 1, fat: sub });
      }
      // Materializa os itens (móvel primeiro, depois fibra; maior valor no topo).
      for (const c of porCnpj.values()) {
        c.itens = Array.from(c._itens.values())
          .sort((a, b) => (a.lado === b.lado ? b.fat - a.fat : a.lado === 'movel' ? -1 : 1));
      }
      const convergentes = Array.from(porCnpj.values()).sort((a, b) => b.fat - a.fat);
      for (const c of convergentes) c.consultor = c.consultor_id ? (nomeById.get(c.consultor_id) ?? null) : null;

      // 6) NEUTROS — CNPJ que JÁ era convergente de ANTES deste mês (já buscado em paralelo).
      const neutroRows = neutroRes.data;
      const porCnpjN = new Map<string, ClienteConvergente & { consultor_id: string | null }>();
      for (const r of (neutroRows as any[]) || []) {
        const sub = Number(r.subtotal || 0);
        const ex = porCnpjN.get(r.cnpj14);
        if (ex) ex.fat += sub;
        else porCnpjN.set(r.cnpj14, {
          cnpj: r.cnpj14, cliente_id: r.cliente_id ?? null, razao: r.razao_social ?? null,
          equipe: r.equipe_nome ?? null, consultor: null, consultor_id: r.consultor_id ?? null, fat: sub, itens: [],
        });
      }
      const neutros = Array.from(porCnpjN.values()).sort((a, b) => b.fat - a.fat);
      for (const c of neutros) c.consultor = c.consultor_id ? (nomeById.get(c.consultor_id) ?? null) : null;

      // 7) NÃO PUNE — SO_MOVEL/SO_FIBRA não penalizado, com o MOTIVO (já buscado em paralelo).
      const npRows = npRes.data;
      const porCnpjNP = new Map<string, ClienteConvergente & { consultor_id: string | null }>();
      for (const r of (npRows as any[]) || []) {
        const sub = Number(r.subtotal || 0);
        const ex = porCnpjNP.get(r.cnpj14);
        if (ex) ex.fat += sub;
        else porCnpjNP.set(r.cnpj14, {
          cnpj: r.cnpj14, cliente_id: r.cliente_id ?? null, pedido_id: r.pedido_id ?? null, razao: r.razao_social ?? null,
          equipe: r.equipe_nome ?? null, consultor: null, consultor_id: r.consultor_id ?? null, fat: sub, itens: [],
          motivo: r.motivo_nao_pune, tipo: r.tipo,
        });
      }
      const naoPune = Array.from(porCnpjNP.values()).sort((a, b) => b.fat - a.fat);
      for (const c of naoPune) c.consultor = c.consultor_id ? (nomeById.get(c.consultor_id) ?? null) : null;

      // DECLARADO SEM PRINT — só o que hoje PAGA bônus (o resto não tem dinheiro
      // em cima). Cada linha é um CNPJ com o botão de reanálise.
      const declPorCnpj = new Map<string, any>();
      for (const r of ((declRes.data as any[]) || [])) declPorCnpj.set(r.cnpj14, r);
      const declaradoSemPrint: ClienteConvergente[] = convergentes
        .filter((c) => declPorCnpj.has(c.cnpj))
        .map((c) => ({ ...c, contrato: declPorCnpj.get(c.cnpj)?.contrato_declarado ?? null } as ClienteConvergente));

      const perda = fatPenalizavel * (penalidadePct / 100);
      const ganho = fatBonificavel * (bonusPct / 100);

      return {
        penalidadePct,
        fatPenalizavel,
        perda,
        pedidos,
        nPedidos: pedidos.length,
        mesRef: mes,
        bonusPct,
        fatBonificavel,
        ganho,
        convergentes,
        nConvergentes: convergentes.length,
        saldo: ganho - perda,
        neutros,
        nNeutros: neutros.length,
        naoPune,
        nNaoPune: naoPune.length,
        declaradoSemPrint,
        nDeclaradoSemPrint: declaradoSemPrint.length,
        fatDeclaradoSemPrint: declaradoSemPrint.reduce((s2, c) => s2 + (c.fat || 0), 0),
      };
    },
  });
}
